import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { lineCells, neighbors, rowColLabel } from '@campfire/grid';

export const SIZE = 10;
/** The classic Hasbro fleet, in tray order. A ship's id is its index here. */
export const FLEET = [
  { name: 'Carrier', length: 5 },
  { name: 'Battleship', length: 4 },
  { name: 'Cruiser', length: 3 },
  { name: 'Submarine', length: 3 },
  { name: 'Destroyer', length: 2 },
] as const;
export const SEAT_NAMES = ['Player 1', 'Player 2'] as const;
export const squareName = (i: number): string => rowColLabel(SIZE, i);

export interface BattleshipOptions {
  /** Ships may sit side by side (Hasbro). Off: every ship needs a gap of one square, corners included. */
  readonly touching: boolean;
  /** Each turn fires as many shots as you have ships afloat, as one action. */
  readonly salvo: boolean;
}

export interface Ship {
  /** Index into FLEET. */
  readonly id: number;
  /** Squares, first to last, running right (same row) or down (same column). */
  readonly cells: readonly number[];
}

export type ShotResult = 'miss' | 'hit' | 'sunk';
export interface Shot {
  readonly sq: number;
  readonly result: ShotResult;
  /** The ship id, when the shot sank it. */
  readonly ship?: number;
}

export interface BattleshipState {
  readonly options: BattleshipOptions;
  readonly phase: 'setup' | 'battle' | 'over';
  /** Each seat's placed ships (secret until sunk or the game ends). */
  readonly fleets: readonly [readonly Ship[], readonly Ship[]];
  readonly ready: readonly [boolean, boolean];
  /** Shots each seat has fired at the other, in order, with their results. */
  readonly shots: readonly [readonly Shot[], readonly Shot[]];
  readonly turn: Seat;
  /** The last volley and who fired it. */
  readonly last: { readonly seat: Seat; readonly shots: readonly Shot[] } | null;
  readonly moves: number;
  readonly result: Outcome | null;
}

export type BattleshipAction =
  | { readonly type: 'place'; readonly ship: number; readonly cell: number; readonly vertical: boolean }
  | { readonly type: 'unplace'; readonly ship: number }
  | { readonly type: 'clear' }
  | { readonly type: 'randomize' }
  | { readonly type: 'ready' }
  | { readonly type: 'fire'; readonly squares: readonly number[] }
  | { readonly type: 'resign' };

export interface BattleshipView {
  readonly you: Seat | null;
  readonly options: BattleshipOptions;
  readonly phase: 'setup' | 'battle' | 'over';
  readonly turn: Seat | null;
  /** Ships the viewer may see per seat: all of their own, only sunk ones of the enemy's, everything once the game is over. */
  readonly fleets: readonly [readonly ShipView[], readonly ShipView[]];
  readonly ready: readonly [boolean, boolean];
  readonly shots: readonly [readonly Shot[], readonly Shot[]];
  readonly afloat: readonly [number, number];
  /** Shots the player to move must fire this turn. */
  readonly volley: number;
  readonly last: BattleshipState['last'];
  readonly moves: number;
  readonly result: Outcome | null;
}

export interface ShipView extends Ship {
  readonly sunk: boolean;
}

const other = (s: Seat): Seat => 1 - s;
/** Tuple element for a seat (indexing by a variable seat is `T | undefined` under strict index checks). */
const of = <T>(p: readonly [T, T], seat: Seat): T => p[seat] as T;
const pair = <T>(a: T, b: T): readonly [T, T] => [a, b];

export const shipOrientation = (ship: Ship): boolean => ship.cells.length > 1 && (ship.cells[1] as number) - (ship.cells[0] as number) === SIZE;

/** The squares a ship would cover, or null when it leaves the board. */
export const shipCells = (id: number, cell: number, vertical: boolean): number[] | null =>
  Number.isInteger(id) && id >= 0 && id < FLEET.length && Number.isInteger(cell) && cell >= 0 && cell < SIZE * SIZE
    ? lineCells(SIZE, cell, vertical ? [0, 1] : [1, 0], (FLEET[id] as { length: number }).length)
    : null;

/** Why a ship cannot go there among the `others`, or null if it can. */
export function placementError(others: readonly Ship[], id: number, cell: number, vertical: boolean, touching: boolean): string | null {
  if (!Number.isInteger(id) || id < 0 || id >= FLEET.length) return 'no such ship';
  const cells = shipCells(id, cell, vertical);
  if (cells === null) return 'that ship does not fit on the board there';
  const taken = new Set(others.filter((o) => o.id !== id).flatMap((o) => o.cells));
  if (cells.some((c) => taken.has(c))) return 'ships cannot overlap';
  if (!touching && cells.some((c) => neighbors(SIZE, c).some((n) => taken.has(n)))) return 'ships may not touch';
  return null;
}

/** A random complete fleet, drawn from the supplied (seeded) rng. Always valid. */
export function randomFleet(rng: Rng, touching: boolean): Ship[] {
  for (;;) {
    const ships: Ship[] = [];
    let stuck = false;
    for (let id = 0; id < FLEET.length && !stuck; id++) {
      let placed = false;
      for (let tries = 0; tries < 200 && !placed; tries++) {
        const vertical = rng.int(2) === 1;
        const cell = rng.int(SIZE * SIZE);
        if (placementError(ships, id, cell, vertical, touching) === null) {
          ships.push({ id, cells: shipCells(id, cell, vertical) as number[] });
          placed = true;
        }
      }
      stuck = !placed;
    }
    if (!stuck) return ships;
  }
}

const sortFleet = (ships: readonly Ship[]): Ship[] => [...ships].sort((a, b) => a.id - b.id);
const fired = (shots: readonly Shot[]): Set<number> => new Set(shots.map((s) => s.sq));

/** Ship ids of `seat`'s fleet that the other seat's shots have sunk. */
function sunkIds(s: BattleshipState, seat: Seat): Set<number> {
  return new Set(of(s.shots, other(seat)).filter((x) => x.result === 'sunk').map((x) => x.ship as number));
}
export const afloatCount = (s: BattleshipState, seat: Seat): number => FLEET.length - sunkIds(s, seat).size;
/** Shots the seat fires this turn: one, or (salvo) one per ship afloat, never more than the squares left. */
export function volleySize(s: BattleshipState, seat: Seat): number {
  const left = SIZE * SIZE - of(s.shots, seat).length;
  return Math.min(s.options.salvo ? afloatCount(s, seat) : 1, left);
}

const allPlaced = (ships: readonly Ship[]) => ships.length === FLEET.length;

const win = (seat: Seat, reason: string, s: BattleshipState): Outcome => ({ winners: [seat], draw: false, reason, scores: { 0: afloatCount(s, 0), 1: afloatCount(s, 1) } });

export const battleship: GameDefinition<BattleshipState, BattleshipAction, BattleshipView, BattleshipOptions> = {
  id: 'battleship',
  name: 'Battleship',
  rules: [
    'Naval combat for two players on a 10x10 sea: rows A to J, columns 1 to 10. Each player secretly hides a fleet, then they take turns firing at each other’s waters until one fleet is sunk.',
    'Your fleet: Carrier (5 squares), Battleship (4), Cruiser (3), Submarine (3) and Destroyer (2). Ships lie straight along a row or column, stay on the board and never overlap.',
    'Setup: both players place their fleets at the same time, in secret. Tap a ship in the tray, then tap a square to put its bow there. Tap the ship again, or press Rotate, to turn it. Randomize places the whole fleet for you; Clear starts over. Press Ready when you are happy. Firing starts when both are ready, and Player 1 fires first.',
    'Your turn: tap a square in Their waters, then press Fire. You are told Miss, Hit, or that you sank a named ship (but not which ship was hit). Turns alternate, and you may not fire at a square twice.',
    'You win by sinking every enemy ship. The app announces every result honestly, and nobody can see the other fleet until the game ends.',
    'Table settings: Ships touching (off means every ship needs a gap of one square, corners included) and Salvo (each turn you fire one shot per ship you still have afloat, all at once).',
    'You can resign at any time. In pass & play, the phone is handed over between turns and during setup so fleets stay hidden. Spectators only see the shots.',
  ],
  minSeats: 2,
  maxSeats: 2,
  defaultOptions: { touching: true, salvo: false },
  optionSpecs: [
    {
      key: 'touching',
      label: 'Ships touching',
      help: 'Classic rules let ships sit side by side.',
      choices: [
        { value: true, label: 'Allowed' },
        { value: false, label: 'Not allowed' },
      ],
    },
    {
      key: 'salvo',
      label: 'Shots per turn',
      help: 'Salvo: fire one shot for every ship you still have afloat.',
      choices: [
        { value: false, label: 'One' },
        { value: true, label: 'Salvo' },
      ],
    },
  ],

  setup: ({ options }) => ({
    options,
    phase: 'setup',
    fleets: [[], []],
    ready: [false, false],
    shots: [[], []],
    turn: 0,
    last: null,
    moves: 0,
    result: null,
  }),

  // Either player may resign at any time; in setup both are due, in battle only the one to fire.
  activeSeats: (s) => (s.phase === 'over' ? [] : [0, 1]),
  waitingOn: (s) => (s.phase === 'over' ? [] : s.phase === 'setup' ? [0, 1].filter((x) => !of(s.ready, x)) : [s.turn]),

  validate(s, a, seat) {
    if (s.phase === 'over') return { ok: false, reason: 'game over' };
    if (seat !== 0 && seat !== 1) return { ok: false, reason: 'not a player' };
    if (a === null || typeof a !== 'object') return { ok: false, reason: 'unknown action' };
    const fleet = of(s.fleets, seat);
    const setupOnly = (): string | null => (s.phase !== 'setup' ? 'the battle has begun' : of(s.ready, seat) ? 'you are already ready' : null);
    switch (a.type) {
      case 'place': {
        const bad = setupOnly();
        if (bad) return { ok: false, reason: bad };
        if (typeof a.vertical !== 'boolean') return { ok: false, reason: 'say whether the ship is vertical' };
        const err = placementError(fleet, a.ship, a.cell, a.vertical, s.options.touching);
        return err ? { ok: false, reason: err } : { ok: true };
      }
      case 'unplace': {
        const bad = setupOnly();
        if (bad) return { ok: false, reason: bad };
        return fleet.some((x) => x.id === a.ship) ? { ok: true } : { ok: false, reason: 'that ship is not placed' };
      }
      case 'clear':
      case 'randomize': {
        const bad = setupOnly();
        return bad ? { ok: false, reason: bad } : { ok: true };
      }
      case 'ready': {
        const bad = setupOnly();
        if (bad) return { ok: false, reason: bad };
        return allPlaced(fleet) ? { ok: true } : { ok: false, reason: 'place every ship first' };
      }
      case 'fire': {
        if (s.phase !== 'battle') return { ok: false, reason: 'the battle has not begun' };
        if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
        if (!Array.isArray(a.squares) || !a.squares.every((q) => Number.isInteger(q) && q >= 0 && q < SIZE * SIZE)) return { ok: false, reason: 'fire at squares on the board' };
        if (new Set(a.squares).size !== a.squares.length) return { ok: false, reason: 'pick each square once' };
        const done = fired(of(s.shots, seat));
        if (a.squares.some((q) => done.has(q))) return { ok: false, reason: 'already fired there' };
        const need = volleySize(s, seat);
        if (a.squares.length !== need) return { ok: false, reason: need === 1 ? 'fire exactly one shot' : `fire exactly ${need} shots` };
        return { ok: true };
      }
      case 'resign':
        return { ok: true };
      default:
        return { ok: false, reason: 'unknown action' };
    }
  },

  apply(s, a, ctx) {
    const seat = ctx.actor as Seat;
    const withFleet = (ships: readonly Ship[]): BattleshipState => ({ ...s, fleets: seat === 0 ? [sortFleet(ships), of(s.fleets, 1)] : [of(s.fleets, 0), sortFleet(ships)] });
    switch (a.type) {
      case 'resign':
        return { ...s, phase: 'over', result: win(other(seat), `${SEAT_NAMES[seat]} resigned`, s) };
      case 'place':
        return withFleet([...of(s.fleets, seat).filter((x) => x.id !== a.ship), { id: a.ship, cells: shipCells(a.ship, a.cell, a.vertical) as number[] }]);
      case 'unplace':
        return withFleet(of(s.fleets, seat).filter((x) => x.id !== a.ship));
      case 'clear':
        return withFleet([]);
      case 'randomize':
        return withFleet(randomFleet(ctx.rng, s.options.touching));
      case 'ready': {
        const ready = seat === 0 ? pair(true, of(s.ready, 1)) : pair(of(s.ready, 0), true);
        return ready[0] && ready[1] ? { ...s, ready, phase: 'battle', turn: 0 } : { ...s, ready };
      }
      case 'fire': {
        const foe = of(s.fleets, other(seat));
        const mine: Shot[] = [...of(s.shots, seat)];
        const volley: Shot[] = [];
        for (const sq of a.squares) {
          const ship = foe.find((x) => x.cells.includes(sq));
          let shot: Shot = { sq, result: ship ? 'hit' : 'miss' };
          mine.push(shot);
          if (ship) {
            const hits = new Set(mine.map((x) => x.sq));
            if (ship.cells.every((c) => hits.has(c))) {
              shot = { sq, result: 'sunk', ship: ship.id };
              mine[mine.length - 1] = shot;
            }
          }
          volley.push(shot);
        }
        const shots = seat === 0 ? pair<readonly Shot[]>(mine, of(s.shots, 1)) : pair<readonly Shot[]>(of(s.shots, 0), mine);
        const next: BattleshipState = { ...s, shots, last: { seat, shots: volley }, moves: s.moves + 1, turn: other(seat) };
        return afloatCount(next, other(seat)) === 0 ? { ...next, phase: 'over', result: win(seat, 'sank the whole fleet', next) } : next;
      }
    }
  },

  view(s, viewer: Viewer): BattleshipView {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const over = s.phase === 'over';
    // Own ships always; the enemy's only once sunk; everything when the game is over.
    const see = (seat: Seat): ShipView[] => {
      const sunk = sunkIds(s, seat);
      return of(s.fleets, seat).filter((x) => over || seat === you || sunk.has(x.id)).map((x) => ({ id: x.id, cells: x.cells, sunk: sunk.has(x.id) }));
    };
    return {
      you,
      options: s.options,
      phase: s.phase,
      turn: s.phase === 'battle' ? s.turn : null,
      fleets: [see(0), see(1)],
      ready: s.ready,
      shots: s.shots,
      afloat: [s.phase === 'setup' ? FLEET.length : afloatCount(s, 0), s.phase === 'setup' ? FLEET.length : afloatCount(s, 1)],
      volley: s.phase === 'battle' ? volleySize(s, s.turn) : 0,
      last: s.last,
      moves: s.moves,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.phase === 'over') return [];
    const out: BattleshipAction[] = [];
    if (s.phase === 'setup' && !of(s.ready, seat)) {
      out.push({ type: 'randomize' });
      if (allPlaced(of(s.fleets, seat))) out.push({ type: 'ready' });
    }
    if (s.phase === 'battle' && seat === s.turn) {
      const done = fired(of(s.shots, seat));
      const open = Array.from({ length: SIZE * SIZE }, (_, i) => i).filter((i) => !done.has(i));
      const k = volleySize(s, seat);
      // One action per square; for a salvo, every run of k consecutive open squares (at most 100 actions).
      for (let i = 0; i + k <= open.length; i++) out.push({ type: 'fire', squares: open.slice(i, i + k) });
    }
    out.push({ type: 'resign' });
    return out;
  },
};
